import { useEffect, useRef, useState } from 'react';
import mapDesign from '@/shared/utils/mapDesign';
import * as Designs from '@pkg/entities/designs';
import * as Library from '@pkg/entities/library';
import * as Revisions from '@pkg/entities/revisions';
import ComparisonColors from '@/lib/theme/comparisonColors';
import { ADDITIONAL_DESIGN } from '@/components/DesignContainer/hooks/slices/data';
import useDesignStore from '@/components/DesignContainer/hooks/useDesignStore';

/**
 * Loads a design and revision from provided ids and adds to 'additional' array
 * in the design store.
 * @returns {Function} loadRevision — awaitable
 */
export default function useAdditionalDesignData() {
  const [designId, setDesignId] = useState(null);
  const [revisionId, setRevisionId] = useState(null);
  const [entity, setEntity] = useState(null);
  const [isActiveComparison, setIsActiveComparison] = useState(false);
  const [trigger, setTrigger] = useState(false);

  const conditions = useDesignStore((state) => state.conditions);
  const settings = useDesignStore((state) => state.settings);
  const library = Library.useLibrary();
  const set = useDesignStore((state) => state.set);

  const pendingRef = useRef(null);

  const { hydrated, isHydrating } = Designs.useHydrated();

  const design =
    hydrated.main?.uuid === designId
      ? hydrated?.main
      : hydrated?.scenarios.map.get(designId);

  const latestId = design?.latest?.uuid;
  const fetchId = revisionId === 'latest' ? latestId : revisionId;

  const { revision, isLoading } = Revisions.useRevision(design?.uuid, fetchId);

  const applyRevision = ({
    design: nextDesign = design,
    revision: nextRevision = revision,
    entity: nextEntity = entity,
    isActiveComparison: nextIsActive = isActiveComparison,
    latestId: nextLatestId = latestId,
  } = {}) => {
    if (!nextDesign || !nextRevision) {
      return null;
    }

    const derivedRevision = Revisions.deriveProps({
      filterConditions: conditions,
      library,
      revision: {
        ...nextRevision,
        is_historical: nextRevision.uuid !== nextLatestId,
      },
      settings,
    });

    const additionalDesign = structuredClone(ADDITIONAL_DESIGN);
    additionalDesign.design = nextDesign;
    additionalDesign.revision = derivedRevision;
    additionalDesign.snapshot = derivedRevision.snapshot;
    additionalDesign.snapshotEntityMap = mapDesign(
      nextDesign,
      additionalDesign.snapshot,
      null,
      true,
      true
    );
    additionalDesign.entity = nextEntity;
    additionalDesign.comparisonColor = ComparisonColors[nextDesign.type];

    const currentAdditional = useDesignStore.getState().additional;
    const updated = currentAdditional.filter(
      (existing) => existing.revision?.uuid !== nextRevision?.uuid
    );
    updated.push(additionalDesign);

    const storeUpdates = { additional: updated };
    if (nextIsActive) {
      storeUpdates.activeComparisonIndex = updated.length - 1;
    }

    set(storeUpdates);
    return additionalDesign;
  };

  const resolvePending = (value) => {
    if (!pendingRef.current) {
      return;
    }
    pendingRef.current.resolve(value);
    pendingRef.current = null;
  };

  const rejectPending = (error) => {
    if (!pendingRef.current) {
      return;
    }
    pendingRef.current.reject(error);
    pendingRef.current = null;
  };

  useEffect(() => {
    if (isHydrating || isLoading || !design || !revision) {
      return;
    }

    const additionalDesign = applyRevision();
    if (additionalDesign) {
      resolvePending(additionalDesign);
    }
  }, [
    JSON.stringify(conditions),
    settings?.activities?.state,
    isHydrating,
    isLoading,
    revision?.uuid,
    trigger,
  ]);

  // Reads the store directly, as callers can hold a stale loadRevision.
  const loadRevision = (
    newDesignId,
    newRevisionId,
    nextEntity,
    nextIsActiveComparison
  ) => {
    const currentAdditional = useDesignStore.getState().additional;
    const index = currentAdditional.findIndex(
      (item) =>
        item.design.uuid === newDesignId && item.revision.uuid === newRevisionId
    );
    if (index !== -1) {
      if (nextIsActiveComparison) {
        set({ activeComparisonIndex: index });
      }
      return Promise.resolve(currentAdditional[index]);
    }

    // Drop any previous waiter so only the latest loadRevision call resolves
    if (pendingRef.current) {
      pendingRef.current.reject(
        new Error('Superseded by a newer loadRevision call')
      );
      pendingRef.current = null;
    }

    const promise = new Promise((resolve, reject) => {
      pendingRef.current = {
        resolve,
        reject,
        designId: newDesignId,
        revisionId: newRevisionId,
      };
    });

    setDesignId(newDesignId);
    setRevisionId(newRevisionId);
    setEntity(nextEntity);
    setIsActiveComparison(nextIsActiveComparison);
    setTrigger((prev) => !prev);

    return promise;
  };

  return loadRevision;
}
